'use client'

import { useState } from 'react'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'

interface Brand {
  slug: string
  name: string
  count: number
}
interface SizeOption {
  value: string
  count: number
}
interface LabeledOption {
  value: string
  label: string
  count: number
}

interface Props {
  brands: Brand[]
  sizes: SizeOption[]
  types: LabeledOption[]
  thickness: LabeledOption[]
  priceBounds: { min: number; max: number }
  activeBrands: string[]
  activeSizes: string[]
  activeTypes: string[]
  activeThickness: string[]
  activeMin: string
  activeMax: string
  hasActiveFilters: boolean
  resetUrl: string
}

const ACCENT = '#FF6B47'
const TEAL = '#1FB8B0'

export function KatalogFilters({
  brands,
  sizes,
  types,
  thickness,
  priceBounds,
  activeBrands,
  activeSizes,
  activeTypes,
  activeThickness,
  activeMin,
  activeMax,
  hasActiveFilters,
  resetUrl,
}: Props) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [open, setOpen] = useState(false) // panel na mobile

  // Buduje URL nadpisując wybrane parametry; zawsze resetuje paginację.
  function buildUrl(overrides: Record<string, string | null>) {
    const params = new URLSearchParams(searchParams.toString())
    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') params.delete(key)
      else params.set(key, value)
    }
    params.delete('strona')
    const qs = params.toString()
    return `${pathname}${qs ? `?${qs}` : ''}`
  }

  // Multi-select: przełącz wartość w liście rozdzielonej przecinkiem.
  function toggle(param: string, current: string[], value: string) {
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    router.push(buildUrl({ [param]: next.length ? next.join(',') : null }))
  }

  function applyPrice(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const min = String(fd.get('min') ?? '').trim()
    const max = String(fd.get('max') ?? '').trim()
    router.push(buildUrl({ cena_min: min || null, cena_max: max || null }))
  }

  const sectionTitle = 'text-xs font-bold uppercase tracking-wider mb-3'
  const sectionStyle = { color: 'rgba(255,255,255,0.4)' }

  // Wspólny chip multi-select (rozmiar / typ / grubość)
  const Chip = ({ param, current, value, label, count }: { param: string; current: string[]; value: string; label: string; count?: number }) => {
    const checked = current.includes(value)
    return (
      <button
        type="button"
        onClick={() => toggle(param, current, value)}
        aria-pressed={checked}
        title={count !== undefined ? `${count} ${count === 1 ? 'produkt' : 'produktów'}` : undefined}
        className="px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all"
        style={checked
          ? { backgroundColor: ACCENT, color: '#03080F', borderColor: ACCENT }
          : { backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.15)' }}
      >
        {label}
      </button>
    )
  }

  const content = (
    <div className="space-y-7">
      {/* Typ (pianki: kamizelka / spodnie / zestawy) */}
      {types.length > 1 && (
        <div>
          <p className={sectionTitle} style={sectionStyle}>Typ</p>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <Chip key={t.value} param="typ" current={activeTypes} value={t.value} label={t.label} count={t.count} />
            ))}
          </div>
        </div>
      )}

      {/* Marka */}
      {brands.length > 1 && (
        <div>
          <p className={sectionTitle} style={sectionStyle}>Marka</p>
          <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
            {brands.map((b) => {
              const checked = activeBrands.includes(b.slug)
              return (
                <button
                  key={b.slug}
                  type="button"
                  onClick={() => toggle('marka', activeBrands, b.slug)}
                  aria-pressed={checked}
                  className="flex items-center gap-2.5 text-left text-sm transition-colors"
                  style={{ color: checked ? '#FFFFFF' : 'rgba(255,255,255,0.7)' }}
                >
                  <span
                    className="flex-shrink-0 w-4 h-4 rounded flex items-center justify-center"
                    style={{
                      backgroundColor: checked ? TEAL : 'transparent',
                      border: `1.5px solid ${checked ? TEAL : 'rgba(255,255,255,0.25)'}`,
                    }}
                  >
                    {checked && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#03080F" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  <span className="flex-1 truncate">{b.name}</span>
                  <span className="text-xs flex-shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }}>{b.count}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Rozmiar */}
      {sizes.length > 0 && (
        <div>
          <p className={sectionTitle} style={sectionStyle}>Rozmiar</p>
          <div className="flex flex-wrap gap-2">
            {sizes.map((s) => (
              <Chip key={s.value} param="rozmiar" current={activeSizes} value={s.value} label={s.value} count={s.count} />
            ))}
          </div>
        </div>
      )}

      {/* Grubość neoprenu (mm) */}
      {thickness.length > 1 && (
        <div>
          <p className={sectionTitle} style={sectionStyle}>Grubość</p>
          <div className="flex flex-wrap gap-2">
            {thickness.map((t) => (
              <Chip key={t.value} param="grubosc" current={activeThickness} value={t.value} label={t.label} count={t.count} />
            ))}
          </div>
        </div>
      )}

      {/* Cena */}
      <div>
        <p className={sectionTitle} style={sectionStyle}>Cena (zł)</p>
        <form onSubmit={applyPrice} className="flex items-center gap-2">
          <input
            type="number" name="min" inputMode="numeric" min={0}
            defaultValue={activeMin} placeholder={String(priceBounds.min)}
            aria-label="Cena od"
            className="w-full min-w-0 px-2.5 py-2 rounded-lg text-sm outline-none focus:ring-1"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.15)', color: '#FFFFFF' }}
          />
          <span style={{ color: 'rgba(255,255,255,0.3)' }}>–</span>
          <input
            type="number" name="max" inputMode="numeric" min={0}
            defaultValue={activeMax} placeholder={String(priceBounds.max)}
            aria-label="Cena do"
            className="w-full min-w-0 px-2.5 py-2 rounded-lg text-sm outline-none focus:ring-1"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.15)', color: '#FFFFFF' }}
          />
          <button
            type="submit"
            aria-label="Zastosuj cenę"
            className="flex-shrink-0 px-3 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}
          >
            OK
          </button>
        </form>
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={() => router.push(resetUrl)}
          className="text-sm font-medium hover:underline"
          style={{ color: ACCENT }}
        >
          Wyczyść filtry
        </button>
      )}
    </div>
  )

  return (
    <div>
      {/* Przełącznik na mobile */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-xl mb-4 text-sm font-semibold"
        style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
      >
        <span className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" />
          </svg>
          Filtry{hasActiveFilters ? ' ·' : ''}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div className={`${open ? 'block' : 'hidden'} lg:block rounded-2xl p-5 mb-6 lg:mb-0`} style={{ backgroundColor: '#0A1828', border: '1px solid rgba(255,255,255,0.07)' }}>
        {content}
      </div>
    </div>
  )
}

const SORT_OPTIONS = [
  { value: 'nazwa', label: 'Nazwa A–Z' },
  { value: 'cena-asc', label: 'Cena: od najniższej' },
  { value: 'cena-desc', label: 'Cena: od najwyższej' },
  { value: 'najnowsze', label: 'Najnowsze' },
]

export function KatalogSort({ activeSort }: { activeSort: string }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  function handleSort(e: React.ChangeEvent<HTMLSelectElement>) {
    const params = new URLSearchParams(searchParams.toString())
    if (e.target.value === 'nazwa') params.delete('sortuj')
    else params.set('sortuj', e.target.value)
    params.delete('strona')
    const qs = params.toString()
    router.push(`${pathname}${qs ? `?${qs}` : ''}`)
  }

  return (
    <select
      value={activeSort}
      onChange={handleSort}
      aria-label="Sortuj produkty"
      className="flex-shrink-0 text-sm rounded-xl px-3 py-2 cursor-pointer"
      style={{ backgroundColor: '#0D1E30', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}
    >
      {SORT_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  )
}
